/**
 * The catalog as an agent should see it: whole items only. Modular kitchens ship their
 * parts (frames, facades, handle layouts, IKEA module leaves) and some cloud items have
 * no name at all — none of those are things a person asks for, and listing them only
 * sends a model chasing them.
 */
import type { CatalogItem } from './agentCatalog';

const PART_TITLE = /^(KitchenCabinet(Facade|Frame|Layout)|HandleLayout|Height\d+ · Ikea|BaseCabinet · Ikea|HighCabinet · Ikea|Primitives)\b/i;
const PART_ID = /\/hl-|-us-(base|wall|high)|\/h\d+-w\d+\//i;
const UNNAMED = /^[0-9a-f]{24}$/i;

export const isCatalogPart = (item: Pick<CatalogItem, 'id' | 'title'>): boolean => PART_TITLE.test(item.title) || PART_ID.test(item.id);
export const isUnnamedItem = (item: Pick<CatalogItem, 'title'>): boolean => UNNAMED.test(item.title.trim());

export function usableCatalogItems<T extends Pick<CatalogItem, 'id' | 'title'>>(items: readonly T[]): T[] {
  return items.filter(item => !isCatalogPart(item) && !isUnnamedItem(item));
}

/** Russian ⇄ English words people and models use for the same furniture; each row is one meaning. */
// i18n-ignore-start: search synonyms, not interface text
export const CATALOG_SYNONYMS: readonly string[][] = [
  ['диван', 'sofa', 'couch'], ['кресло', 'armchair'], ['стул', 'chair'], ['табурет', 'stool'], ['стол', 'table', 'desk'],
  ['журнальный', 'coffee'], ['обеденный', 'dining'], ['кровать', 'bed'], ['тумба', 'nightstand', 'bedside'],
  ['шкаф', 'wardrobe', 'closet', 'cabinet', 'cupboard'], ['полка', 'shelf', 'shelves', 'bookcase', 'стеллаж'], ['комод', 'dresser', 'chest'],
  ['кухня', 'kitchen', 'кухонный', 'гарнитур'], ['плита', 'stove', 'cooker', 'hob', 'oven', 'духовка'], ['холодильник', 'fridge', 'refrigerator'],
  ['мойка', 'раковина', 'sink', 'basin'], ['вытяжка', 'hood', 'микроволновка', 'microwave'], ['посудомойка', 'dishwasher'],
  ['ванна', 'bath', 'bathtub'], ['унитаз', 'toilet', 'wc'], ['душ', 'shower'],
  ['лампа', 'lamp', 'light', 'светильник', 'люстра', 'chandelier', 'торшер'], ['ковёр', 'ковер', 'rug', 'carpet'],
  ['картина', 'picture', 'painting', 'poster', 'постер', 'рамка', 'frame', 'frames'], ['зеркало', 'mirror'], ['растение', 'plant', 'цветок', 'ficus', 'фикус'],
  ['ваза', 'vase'], ['телевизор', 'tv', 'television'], ['штора', 'шторы', 'curtain', 'curtains', 'жалюзи', 'blind', 'blinds'],
  ['дверь', 'door'], ['окно', 'window'], ['арка', 'arch'], ['декор', 'decor', 'статуэтка', 'figurine'],
];
// i18n-ignore-end

/** The query plus every synonym of each of its words, lower-case, unique. */
export function expandQuery(query: string): string[] {
  const words = query.toLowerCase().split(/[\s,;/]+/).filter(Boolean);
  const terms = new Set<string>(words);
  for (const word of words) for (const row of CATALOG_SYNONYMS) if (row.some(entry => entry === word || word.startsWith(entry) || entry.startsWith(word) && word.length >= 4)) row.forEach(entry => terms.add(entry));
  return [...terms];
}
